import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Label } from '../ui/label';
import { HardHat, Plus, Search, Trash2, Edit, Save, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const CRMSubcontractors = () => {
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const emptyForm = { company_name: '', trade: '', specialty: '', contact_name: '', phone: '', email: '', address: '', license_number: '', insurance_expiration: '', dir_status: '', union_status: '', typical_rates: '', performance_rating: '', safety_rating: '', notes: '' };
  const [form, setForm] = useState({ ...emptyForm });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = new URLSearchParams({ limit: '200' });
      if (search) q.set('search', search);
      const res = await fetch(`${API}/api/crm/subcontractors?${q}`, { credentials: 'include' });
      setSubs((await res.json()).subcontractors || []);
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  }, [search]);

  useEffect(() => { load(); }, [load]);

  const handleSave = async () => {
    if (!form.company_name) { toast.error('Company name required'); return; }
    try {
      const url = editId ? `${API}/api/crm/subcontractors/${editId}` : `${API}/api/crm/subcontractors`;
      const res = await fetch(url, { method: editId ? 'PUT' : 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form) });
      if (!res.ok) throw new Error();
      toast.success(editId ? 'Updated' : 'Created');
      setShowForm(false); setEditId(null); setForm({ ...emptyForm }); load();
    } catch { toast.error('Save failed'); }
  };

  const startEdit = s => { setForm({ ...emptyForm, ...s }); setEditId(s.id); setShowForm(true); };
  const handleDelete = async id => { if (!window.confirm('Delete?')) return; await fetch(`${API}/api/crm/subcontractors/${id}`, { credentials: 'include', method: 'DELETE' }); toast.success('Deleted'); load(); };

  const isExpired = date => date && new Date(date) < new Date();
  const isExpiringSoon = date => { if (!date) return false; const d = new Date(date); const now = new Date(); return d > now && d < new Date(now.getTime() + 30 * 86400000); };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center flex-wrap gap-2">
        <p className="text-sm text-gray-500">{subs.length} vendors / subcontractors</p>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input data-testid="sub-search" type="text" placeholder="Search..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9 pr-3 py-1.5 border rounded-lg text-sm w-48" />
          </div>
          <Button size="sm" className="bg-blue-900 hover:bg-blue-800" onClick={() => { setForm({ ...emptyForm }); setEditId(null); setShowForm(true); }} data-testid="add-sub-btn"><Plus size={14} className="mr-1" /> New Vendor / Sub</Button>
        </div>
      </div>

      {showForm && (
        <Card className="border-blue-200">
          <CardContent className="pt-5 space-y-3">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="col-span-2"><Label className="text-xs text-slate-500">Company Name *</Label><input value={form.company_name} onChange={e => setForm(f => ({ ...f, company_name: e.target.value }))} className="w-full px-3 py-2 border rounded-lg text-sm" data-testid="sub-company-name" /></div>
              <div><Label className="text-xs text-slate-500">Trade</Label><input value={form.trade} onChange={e => setForm(f => ({ ...f, trade: e.target.value }))} className="w-full px-3 py-2 border rounded-lg text-sm" /></div>
              <div><Label className="text-xs text-slate-500">Specialty</Label><input value={form.specialty} onChange={e => setForm(f => ({ ...f, specialty: e.target.value }))} className="w-full px-3 py-2 border rounded-lg text-sm" /></div>
              <div><Label className="text-xs text-slate-500">Contact Name</Label><input value={form.contact_name} onChange={e => setForm(f => ({ ...f, contact_name: e.target.value }))} className="w-full px-3 py-2 border rounded-lg text-sm" /></div>
              <div><Label className="text-xs text-slate-500">Phone</Label><input value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} className="w-full px-3 py-2 border rounded-lg text-sm" /></div>
              <div><Label className="text-xs text-slate-500">Email</Label><input value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} className="w-full px-3 py-2 border rounded-lg text-sm" /></div>
              <div><Label className="text-xs text-slate-500">License #</Label><input value={form.license_number} onChange={e => setForm(f => ({ ...f, license_number: e.target.value }))} className="w-full px-3 py-2 border rounded-lg text-sm" /></div>
              <div><Label className="text-xs text-slate-500">Insurance Exp.</Label><input type="date" value={form.insurance_expiration || ''} onChange={e => setForm(f => ({ ...f, insurance_expiration: e.target.value }))} className="w-full px-3 py-2 border rounded-lg text-sm" data-testid="sub-insurance-exp" /></div>
              <div><Label className="text-xs text-slate-500">DIR Status</Label><input value={form.dir_status} onChange={e => setForm(f => ({ ...f, dir_status: e.target.value }))} className="w-full px-3 py-2 border rounded-lg text-sm" /></div>
              <div><Label className="text-xs text-slate-500">Union Status</Label><input value={form.union_status} onChange={e => setForm(f => ({ ...f, union_status: e.target.value }))} className="w-full px-3 py-2 border rounded-lg text-sm" /></div>
              <div><Label className="text-xs text-slate-500">Typical Rates</Label><input value={form.typical_rates} onChange={e => setForm(f => ({ ...f, typical_rates: e.target.value }))} className="w-full px-3 py-2 border rounded-lg text-sm" /></div>
            </div>
            <div><Label className="text-xs text-slate-500">Notes</Label><textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} rows={2} className="w-full px-3 py-2 border rounded-lg text-sm" /></div>
            <div className="flex gap-2">
              <Button size="sm" className="bg-blue-900 hover:bg-blue-800" onClick={handleSave} data-testid="save-sub-btn"><Save size={14} className="mr-1" /> {editId ? 'Update' : 'Create'}</Button>
              <Button size="sm" variant="outline" onClick={() => { setShowForm(false); setEditId(null); }}>Cancel</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {loading ? <div className="text-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-900 mx-auto" /></div>
      : subs.length === 0 ? <Card><CardContent className="py-12 text-center text-gray-400"><HardHat className="mx-auto mb-2" size={32} /><p>No vendors / subcontractors yet</p></CardContent></Card>
      : <div className="space-y-2">{subs.map(s => (
        <Card key={s.id} data-testid={`sub-row-${s.id}`}>
          <CardContent className="py-3 px-4">
            <div className="flex items-center justify-between">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-slate-900">{s.company_name}</span>
                  {s.trade && <Badge className="text-[10px] bg-blue-50 text-blue-700">{s.trade}</Badge>}
                  {isExpired(s.insurance_expiration) && <Badge className="text-[10px] bg-red-100 text-red-700 flex items-center gap-0.5"><AlertTriangle size={10} /> Ins. Expired</Badge>}
                  {isExpiringSoon(s.insurance_expiration) && <Badge className="text-[10px] bg-amber-100 text-amber-700">Ins. Expiring</Badge>}
                </div>
                <div className="flex gap-3 text-xs text-gray-500 mt-0.5">
                  {s.contact_name && <span>{s.contact_name}</span>}
                  {s.phone && <span>{s.phone}</span>}
                  {s.license_number && <span>Lic: {s.license_number}</span>}
                </div>
              </div>
              <div className="flex gap-1 flex-shrink-0">
                <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => startEdit(s)}><Edit size={13} /></Button>
                <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-400" onClick={() => handleDelete(s.id)}><Trash2 size={13} /></Button>
              </div>
            </div>
          </CardContent>
        </Card>
      ))}</div>}
    </div>
  );
};

export default CRMSubcontractors;
